import {
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Separator,
  Select,
  Switch,
  Text,
  TextField,
  TextArea,
  Heading,
} from "@radix-ui/themes"
import { AlertCircle, MapPin, Globe, Image as ImageIcon } from "lucide-react"
import { SubmitEvent } from "react"
import TrixField from "@/shared/primitives/TrixField"

export interface TrackFormData {
  track: {
    name: string
    street: string
    city: string
    state: string
    country: string
    zip: string
    time_zone: string
    latitude: string
    longitude: string
    instagram: string
    website_url: string
    google_map_url: string
    instagram_hashtag: string
    rules: string
    prices: string
    parking: string
    connectivity: string
    transportation: string
    accessibility: string
    family_info: string
    prohibited_items: string
    pit_access: string
    /** Auto-approve media who already hold an approved annual credential here.
     *  With no credential forms published there is nothing to check, so the
     *  switch alone approves media up to the number below. */
    media_auto_approval: boolean
    /** How many media the auto path may approve per event. Counts
     *  auto-approvals only — hand approvals never fill a slot. */
    media_auto_approval_limit: number
    venue_map: File | null
  }
}

export interface TrackTimeZoneOption {
  value: string
  label: string
}

interface TrackFormProps {
  data: TrackFormData
  setData: <K extends keyof TrackFormData>(key: K, value: TrackFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void
  processing: boolean
  errors?: { [key: string]: string[] }
  title: string
  submitLabel: string
  hasVenueMap?: boolean
  timeZones: TrackTimeZoneOption[]
}

export default function TrackForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  title,
  submitLabel,
  hasVenueMap,
  timeZones,
}: TrackFormProps) {
  const setTrack = (fields: Partial<TrackFormData["track"]>) => {
    setData("track", { ...data.track, ...fields })
  }

  const errorMessages = errors ? Object.values(errors).flat() : []

  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        Fill in the details below to create or edit a track.
      </Text>
      <Separator my="3" size="4" />

      {errorMessages.length > 0 && (
        <Callout.Root color="red" role="alert" mb="4">
          {errorMessages.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}

      <form onSubmit={onSubmit}>
        {/* Basic Info */}
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Track Name *
              </Text>
              <TextField.Root
                size="3"
                value={data.track.name}
                onChange={(e) => setTrack({ name: e.target.value })}
                required
                placeholder="e.g. The Circuit at Las Vegas"
              />
            </Flex>
          </Box>

          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  City *
                </Text>
                <TextField.Root
                  size="3"
                  value={data.track.city}
                  onChange={(e) => setTrack({ city: e.target.value })}
                  required
                  placeholder="e.g. Las Vegas"
                />
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  State/Province *
                </Text>
                <TextField.Root
                  size="3"
                  value={data.track.state}
                  onChange={(e) => setTrack({ state: e.target.value })}
                  required
                  placeholder="e.g. NV"
                />
              </Flex>
            </Box>
          </Grid>

          <Grid columns="2" gap="5">
            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  Country *
                </Text>
                <TextField.Root
                  size="3"
                  value={data.track.country}
                  onChange={(e) => setTrack({ country: e.target.value })}
                  required
                  placeholder="e.g. USA"
                />
              </Flex>
            </Box>

            <Box>
              <Flex gap="2" direction="column">
                <Text size="3" weight="medium">
                  ZIP/Postal Code
                </Text>
                <TextField.Root
                  size="3"
                  value={data.track.zip}
                  onChange={(e) => setTrack({ zip: e.target.value })}
                  placeholder="e.g. 89104"
                />
              </Flex>
            </Box>
          </Grid>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Street Address
              </Text>
              <TextField.Root
                size="3"
                value={data.track.street}
                onChange={(e) => setTrack({ street: e.target.value })}
                placeholder="e.g. 123 Racing Blvd"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Coordinates */}
        <Text size="5" weight="bold" mb="4">
          Location
        </Text>
        <Grid columns="1" gap="5" mb="5">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Time Zone *
              </Text>
              <Text size="2" color="gray">
                Decides when an event's applications open and close — the window ends at
                midnight here, not wherever the applicant happens to be.
              </Text>
              <Select.Root
                size="3"
                value={data.track.time_zone || undefined}
                onValueChange={(v) => setTrack({ time_zone: v })}
                required
              >
                <Select.Trigger placeholder="Select a time zone…" />
                <Select.Content>
                  {timeZones.map((zone) => (
                    <Select.Item key={zone.value} value={zone.value}>
                      {zone.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          </Box>
        </Grid>
        <Grid columns="2" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Latitude
              </Text>
              <TextField.Root
                size="3"
                type="number"
                step="0.000001"
                value={data.track.latitude}
                onChange={(e) => setTrack({ latitude: e.target.value })}
                placeholder="e.g. 36.1699"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Longitude
              </Text>
              <TextField.Root
                size="3"
                type="number"
                step="0.000001"
                value={data.track.longitude}
                onChange={(e) => setTrack({ longitude: e.target.value })}
                placeholder="e.g. -115.1398"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Links and Social */}
        <Text size="5" weight="bold" mb="4">
          Links & Social Media
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Website URL
              </Text>
              <TextField.Root
                size="3"
                type="url"
                value={data.track.website_url}
                onChange={(e) => setTrack({ website_url: e.target.value })}
                placeholder="https://example.com"
              >
                <TextField.Slot>
                  <Globe height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Google Maps URL
              </Text>
              <TextField.Root
                size="3"
                type="url"
                value={data.track.google_map_url}
                onChange={(e) => setTrack({ google_map_url: e.target.value })}
                placeholder="https://maps.google.com/..."
              >
                <TextField.Slot>
                  <MapPin height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Instagram Handle
              </Text>
              <TextField.Root
                size="3"
                value={data.track.instagram}
                onChange={(e) => setTrack({ instagram: e.target.value })}
                placeholder="e.g. @trackname"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Instagram Hashtag
              </Text>
              <TextField.Root
                size="3"
                value={data.track.instagram_hashtag}
                onChange={(e) => setTrack({ instagram_hashtag: e.target.value })}
                placeholder="e.g. #trackname"
              />
            </Flex>
          </Box>
        </Grid>

        {/* Content */}
        <Text size="5" weight="bold" mb="4">
          Content
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Rules
              </Text>
              <TextArea
                size="3"
                value={data.track.rules}
                onChange={(e) => setTrack({ rules: e.target.value })}
                placeholder="Track rules and requirements..."
                style={{ minHeight: "120px" }}
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">
                Prices
              </Text>
              <TextArea
                size="3"
                value={data.track.prices}
                onChange={(e) => setTrack({ prices: e.target.value })}
                placeholder="Pricing information..."
                style={{ minHeight: "120px" }}
              />
            </Flex>
          </Box>
        </Grid>

        {/* Media Applications */}
        <Text size="5" weight="bold" mb="4">
          Media Applications
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text as="label" size="3" weight="medium">
                <Flex gap="2" align="center">
                  <Switch
                    checked={data.track.media_auto_approval}
                    onCheckedChange={(checked) =>
                      setTrack({ media_auto_approval: checked })
                    }
                  />
                  Auto-approve credentialed media
                </Flex>
              </Text>
              <Text size="2" color="gray">
                Media who already hold an approved annual credential here are approved
                the moment they apply, up to the number below. If this track
                doesn&rsquo;t publish credential forms there&rsquo;s nothing to check,
                so every media applicant is approved up to that number. Credentials
                themselves are never auto-approved &mdash; a manager still reviews each
                one.
              </Text>
            </Flex>
          </Box>

          {/* Only meaningful once the switch is on, so it follows it rather than
              sitting in its own section. */}
          {data.track.media_auto_approval && (
            <Box>
              <Flex gap="2" direction="column">
                <Text as="label" size="3" weight="medium">
                  Auto-approved media per event *
                </Text>
                <TextField.Root
                  size="3"
                  type="number"
                  min="1"
                  required
                  style={{ maxWidth: 160 }}
                  value={data.track.media_auto_approval_limit}
                  onChange={(e) =>
                    setTrack({
                      media_auto_approval_limit: Math.max(0, Number(e.target.value)),
                    })
                  }
                />
                <Text size="2" color="gray">
                  Counts auto-approvals only &mdash; managers can keep approving media by
                  hand past this. Media who apply once it&rsquo;s full are waitlisted, and
                  the first one still eligible is approved automatically if a spot frees
                  up. Events can override this individually.
                </Text>
              </Flex>
            </Box>
          )}
        </Grid>

        {/* What to Expect at the Venue */}
        <Text size="5" weight="bold" mb="4">
          What to Expect at the Venue
        </Text>
        <Text size="2" color="gray" as="p" mb="4">
          These describe the venue itself — they show on the track page and are inlined on every event at this track. Events can override the most-often-changing ones individually.
        </Text>
        <Grid columns="1" gap="5" mb="6">
          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Parking</Text>
              <TrixField
                value={data.track.parking}
                onChange={(html) => setTrack({ parking: html })}
                placeholder="Where to park, fees, in/out privileges…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Pit Access (default protocol)</Text>
              <TrixField
                value={data.track.pit_access}
                onChange={(html) => setTrack({ pit_access: html })}
                placeholder="Spectator pit access rules, times, wristbands…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Connectivity</Text>
              <TrixField
                value={data.track.connectivity}
                onChange={(html) => setTrack({ connectivity: html })}
                placeholder="Wi‑Fi availability, cell coverage on property…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Transportation</Text>
              <TrixField
                value={data.track.transportation}
                onChange={(html) => setTrack({ transportation: html })}
                placeholder="Closest airports, shuttle info, rideshare, parking distance…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Accessibility</Text>
              <TrixField
                value={data.track.accessibility}
                onChange={(html) => setTrack({ accessibility: html })}
                placeholder="ADA-friendly viewing areas, restrooms, mobility considerations…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Family Info</Text>
              <TrixField
                value={data.track.family_info}
                onChange={(html) => setTrack({ family_info: html })}
                placeholder="Kid-friendly amenities, stroller access, shade…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Prohibited Items</Text>
              <TrixField
                value={data.track.prohibited_items}
                onChange={(html) => setTrack({ prohibited_items: html })}
                placeholder="Coolers, outside food, pets, drones…"
              />
            </Flex>
          </Box>

          <Box>
            <Flex gap="2" direction="column">
              <Text size="3" weight="medium">Venue Map</Text>
              <Text size="2" color="gray">Image displayed on the track and event pages.</Text>
              {hasVenueMap && (
                <Text size="2" color="indigo">Current venue map attached — upload below to replace it.</Text>
              )}
              <Flex align="center" gap="2">
                <ImageIcon height="16" width="16" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setTrack({ venue_map: e.target.files?.[0] ?? null })}
                  style={{ fontSize: "var(--font-size-2)" }}
                />
              </Flex>
            </Flex>
          </Box>
        </Grid>

        {/* Submit */}
        <Flex gap="3" justify="end" pt="4">
          <Button size="3" type="submit" disabled={processing}>
            {submitLabel}
          </Button>
        </Flex>
      </form>
    </Box>
  )
}
